/**
 * The board engine for a record-backed tracker: rows, groups and every number
 * come from the rows endpoint rather than from an array held in the browser.
 *
 * This is the counterpart to `useTrackersBoard`, which loads a board's whole
 * contents and then filters, groups, sorts and counts over it. That works while
 * a board is small and stops being true the moment it is not, which is why the
 * two engines are separate files rather than one hook with a source switch:
 * every question the client answers there is a query parameter here, and every
 * count is the server's answer over the whole matching set.
 *
 * The rule that shapes the rest: **no number on a record board is derived from
 * the loaded page.** A group header, a Kanban column, a filter chip and the
 * total all read `total` / `valueCounts` off the response. Counting the rows in
 * hand would produce a figure that shrinks as you scroll and disagrees with the
 * one beside it, which is worse than no figure at all.
 */
import { useCallback, useMemo, useState } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  TRACKER_QUERY_KEYS,
  trackerRowsQueryOptions,
  useUpdateTrackerRowMutation,
  type Tracker,
  type TrackerRow,
  type TrackerRowUser,
} from '@/entities/tracker';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { notificationService } from '@/lib/services/notification.service';
import { useDebounce } from '@/shared/hooks';

import { CUSTOM_COLUMN_MIN_WIDTH_BY_TYPE, type CustomColumn } from './board-columns';
import type { FilterColumnSpec, FilterValueCounts } from './filtering';
import { NO_GROUP_COLOR, NO_GROUP_ID } from './grouping';
import {
  groupableFields,
  isRecordSortable,
  recordFieldsFor,
  titleFieldFor,
  WORKSPACE_FIELD,
  type RecordField,
} from './record-fields';
import {
  buildRecordFilterColumns,
  buildRowsQuery,
  countsForField,
  filtersWithinScope,
  NO_VALUE,
  normaliseRecordFilters,
  serverFilterValueCounts,
  valueLabel,
  type RecordLabels,
  type ServerValueCounts,
} from './record-query';
import { isForeignRow } from './record-rows';
import { recordStatusOptions } from './record-status-options';
import { useRecordUserDirectory } from './record-users';
import { cycleColumnSort, type ColumnSort } from './sorting';
import { useBoardLayout, type BoardLayout, type ColumnDef } from './useBoardLayout';
import { useFilterOptionLabels } from './useFilterOptionLabels';
import { usePersistentFilters } from './usePersistentFilters';
import { useWorkspaceScope } from './useWorkspaceScope';
import { rowWriteRefusalNotice } from './row-write-refusal';

/** Typing pause before a keystroke becomes a request. */
const SEARCH_DEBOUNCE_MS = 300;

/** Toolbar sort ids are `<fieldId>-asc` / `<fieldId>-desc`. */
const SORT_ID_SEPARATOR = '-';

/** One group as the board renders it, counted by the server. */
export interface RecordGroup {
  id: string;
  title: string;
  color?: string;
  rows: TrackerRow[];
  /** The server's count for this bucket over the whole matching set. */
  count: number;
}

export interface RecordBoard {
  /**
   * The saved layout, plus the workspace column while the board reads several
   * workspaces. That column is added on the way out and never reaches the
   * layout's own state, so no save can write it into the shared layout.
   */
  layout: BoardLayout;
  /** The record's fields, and the workspace field while its column shows. */
  fields: readonly RecordField[];
  /**
   * Every column the row and Kanban renderers have to draw: the record's own
   * fields first, then the board's custom columns.
   *
   * Derived for rendering only. A record field is deliberately NOT written into
   * `layout.customColumns`, which is what the column header's Duplicate and
   * Configure items read: finding one there would let a user mint a broken uuid
   * copy of a platform field, or overwrite the platform's status set with a
   * local one. Not finding one is why both quietly do nothing today.
   */
  customColumns: CustomColumn[];
  titleField: string | null;

  rows: TrackerRow[];
  groups: RecordGroup[];
  /** Rows matching the current query, server-counted. */
  total: number;
  /**
   * Who the user ids on this board belong to, resolved org-wide by the server.
   * The workspace roster cannot answer for a record whose creator has left, so
   * the cells read this instead. See `record-users`.
   */
  users: ReadonlyMap<string, TrackerRowUser>;

  /** No answer yet: either the first request or the layout that shapes it. */
  isLoading: boolean;
  /** The request failed, so the board knows nothing rather than nothing matching. */
  isError: boolean;
  /** The query has settled and the board genuinely holds nothing. */
  isEmpty: boolean;
  /** Settled and empty, but only because the search or filters narrowed it. */
  isFilteredEmpty: boolean;
  isFetching: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  refresh: () => void;

  searchInput: string;
  setSearchInput: (next: string) => void;
  filters: Record<string, readonly string[]>;
  setFilters: (next: Record<string, readonly string[]>) => void;
  filterColumns: FilterColumnSpec[];
  filterValueCounts: FilterValueCounts;
  /**
   * Filterable fields the request had no `countBy` slot left for. Their chips
   * render without a number rather than with a stale or invented one.
   */
  uncountedFieldIds: readonly string[];

  groupBy: string | null;
  setGroupBy: (next: string | null) => void;
  groupByOptions: Array<{ value: string; label: string }>;

  sortId: string | null;
  setSortId: (next: string | null) => void;
  columnSort: ColumnSort | null;
  toggleColumnSort: (columnId: string) => void;

  /** The workspace this board belongs to. A row from any other is foreign. */
  boardWorkspaceId: string;
  /** A workspace id as the name its cell and filter show. */
  workspaceLabel: (id: string) => string;

  /** Write one cell back to the source record. No-op for a read-only cell. */
  setCellValue: (rowKey: string, columnId: string, value: unknown) => void;
  /**
   * Whether this column accepts an edit at all or, given a row, whether that
   * row's cell does: a record field also needs the row's `canEdit`, which the
   * server sets from the caller's rights on the record. The board's own
   * columns follow the board's access level alone, except on a row from
   * another workspace, where every cell needs `canEdit`.
   */
  isEditable: (columnId: string, rowKey?: string) => boolean;
  /**
   * Whether clicking this column's header can actually order the board. Only
   * the server sorts here, and it sorts by record field, so a custom column
   * answers `false` however sortable its type looks.
   */
  isSortable: (columnId: string) => boolean;
  /** Whether the column header's kebab offers the actions that reshape a column. */
  columnMenu: (columnId: string) => RecordColumnMenu;
}

/**
 * Which header-menu actions a column on a record board can honour.
 *
 * A record field is deliberately absent from `layout.customColumns`, so Delete,
 * Duplicate and Configure all miss the lookup and quietly do nothing. Saying so
 * here is what turns three inert menu items into three that are not offered.
 */
export interface RecordColumnMenu {
  rename: boolean;
  duplicate: boolean;
  remove: boolean;
  configure: boolean;
}

/** Everything a column the board itself owns can do. */
const CUSTOM_COLUMN_MENU: RecordColumnMenu = {
  rename: true,
  duplicate: true,
  remove: true,
  configure: true,
};

/**
 * A platform field can be renamed - the label is the board's, and
 * `layout.columnLabels` holds the override - but its shape belongs to the
 * source, so nothing that would copy, drop or reconfigure it is offered.
 */
const RECORD_FIELD_MENU: RecordColumnMenu = {
  rename: true,
  duplicate: false,
  remove: false,
  configure: false,
};

/** The workspace column is not in the layout at all, so not even a rename can be kept. */
const WORKSPACE_COLUMN_MENU: RecordColumnMenu = {
  rename: false,
  duplicate: false,
  remove: false,
  configure: false,
};

/**
 * Where the workspace column sits: right after the record's name, which is
 * the sticky Item column unless the layout also lists the title field.
 */
function withWorkspaceColumn(columnOrder: readonly string[], titleField: string | null): string[] {
  const at = titleField ? columnOrder.indexOf(titleField) + 1 : 0;
  return [...columnOrder.slice(0, at), WORKSPACE_FIELD.id, ...columnOrder.slice(at)];
}

export function useRecordBoard(tracker: Tracker): RecordBoard {
  const { t } = useTranslation('trackers');
  const queryClient = useQueryClient();
  const workspaceId = useCurrentWorkspaceId() ?? '';
  const trackerId = tracker.id;

  const fields = useMemo(() => recordFieldsFor(tracker.itemSource), [tracker.itemSource]);
  const titleField = useMemo(() => titleFieldFor(tracker.itemSource), [tracker.itemSource]);

  // A record board's columns are the record's own fields, so the layout hook is
  // told about them: they are legal ids in a saved `columnOrder`, and without
  // them the seed-time sanitiser would read every one as stale and empty the
  // board's column list on first load.
  const builtInColumns = useMemo<Record<string, ColumnDef>>(
    () =>
      Object.fromEntries(
        fields.map((field) => [
          field.id,
          {
            label: t(field.labelKey),
            minWidth: CUSTOM_COLUMN_MIN_WIDTH_BY_TYPE[field.type],
            type: field.type,
          },
        ]),
      ),
    [fields, t],
  );
  const layout = useBoardLayout({ tracker, builtInColumns });

  const workspaceScope = useWorkspaceScope(trackerId);
  const { rowsScope } = workspaceScope;
  const widensScope = rowsScope !== 'board';
  // Any scope beyond the board may read several workspaces, so the request is
  // shaped for the workspace column before the answer says whether it shows.
  // Waiting for that answer would spend a second request on every such board.
  const queryFields = useMemo(
    () => (widensScope ? [...fields, WORKSPACE_FIELD] : fields),
    [widensScope, fields],
  );
  const queryColumnOrder = useMemo(
    () => (widensScope ? withWorkspaceColumn(layout.columnOrder, titleField) : layout.columnOrder),
    [widensScope, layout.columnOrder, titleField],
  );

  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);
  const [sortId, setSortIdState] = useState<string | null>(null);
  const [columnSort, setColumnSort] = useState<ColumnSort | null>(null);
  // Per-user, per-browser, like both client boards. See usePersistentFilters.
  const [storedFilters, setStoredFilters] = usePersistentFilters(trackerId);

  /**
   * Selections trimmed to what the endpoint can actually carry, so the chips
   * and the query read the same state. A date selection that survives here is
   * one bucket, because two `gte`/`lte` pairs on one field AND together and
   * would silently match nothing.
   */
  const resolvedWorkspaceIds = workspaceScope.scope?.resolvedWorkspaceIds;
  const filters = useMemo(
    () =>
      filtersWithinScope(normaliseRecordFilters(storedFilters, queryFields), resolvedWorkspaceIds),
    [storedFilters, queryFields, resolvedWorkspaceIds],
  );

  const groupBy = layout.groupById;

  /**
   * One instant for the whole board. Two `new Date()` calls can straddle
   * midnight, and the pair would then compile a date bucket whose two
   * boundaries disagree about which day "today" is.
   */
  const now = useMemo(() => new Date(), []);

  const sortState = useMemo(() => {
    if (columnSort) return { sortBy: columnSort.columnId, sortOrder: columnSort.dir };
    if (!sortId) return { sortBy: null, sortOrder: 'asc' as const };
    const at = sortId.lastIndexOf(SORT_ID_SEPARATOR);
    const order = sortId.slice(at + 1);
    return {
      sortBy: sortId.slice(0, at),
      sortOrder: order === 'desc' ? ('desc' as const) : ('asc' as const),
    };
  }, [sortId, columnSort]);

  const plan = useMemo(
    () =>
      buildRowsQuery(
        {
          search,
          filters,
          groupBy,
          sortBy: sortState.sortBy,
          sortOrder: sortState.sortOrder,
        },
        queryFields,
        queryColumnOrder,
        now,
      ),
    [search, filters, groupBy, sortState, queryFields, queryColumnOrder, now],
  );

  const rowsParams = useMemo(
    () => ({ ...plan.params, scope: rowsScope }),
    [plan.params, rowsScope],
  );

  // Held until the saved layout and the workspace scope are in: both shape the
  // request (`columnOrder` decides what the server is asked to count), so
  // querying before either lands spends a round trip and throws the answer away.
  const rowsQuery = useInfiniteQuery({
    ...trackerRowsQueryOptions(workspaceId, trackerId, rowsParams),
    enabled:
      layout.hasSeeded && workspaceScope.settled && Boolean(workspaceId) && Boolean(trackerId),
  });

  const pages = useMemo(() => rowsQuery.data?.pages ?? [], [rowsQuery.data]);
  const rows = useMemo(() => pages.flatMap((page) => page.data), [pages]);

  /**
   * Totals ride on every page and describe the same matching set, so the first
   * one is as good as the last. Reading the first specifically keeps the number
   * steady while a later page is in flight, instead of flickering to whatever
   * the newest response happened to carry.
   */
  const total = pages[0]?.total ?? 0;
  const valueCounts = pages[0]?.valueCounts as ServerValueCounts | undefined;

  const users = useRecordUserDirectory(pages);

  const workspaces = pages[0]?.workspaces;
  const showsWorkspace = widensScope && (workspaces?.length ?? 0) > 1;
  const shownFields = showsWorkspace ? queryFields : fields;
  const fieldById = useMemo(
    () => new Map(shownFields.map((field) => [field.id, field])),
    [shownFields],
  );

  const { columnLabels } = layout;
  const renderColumns = useMemo<CustomColumn[]>(
    () => [
      ...shownFields.map((field) => ({
        id: field.id,
        // The rename override first, so a Kanban card names a column the same
        // way its table header does.
        label: columnLabels[field.id] ?? t(field.labelKey),
        type: field.type,
        clearable: field.nullable === true,
        // A status cell and a Kanban lane both need the option set, and a
        // record field's belongs to the platform rather than to the layout.
        ...(field.type === 'status'
          ? { statusOptions: recordStatusOptions(tracker.itemSource, field.id) }
          : {}),
      })),
      ...layout.customColumns,
    ],
    [shownFields, layout.customColumns, columnLabels, tracker.itemSource, t],
  );

  const unnamedWorkspace = t('trackersBoard.workspaceScope.unnamedWorkspace');
  const workspaceLabel = useCallback(
    (id: string) => workspaces?.find((workspace) => workspace.id === id)?.name ?? unnamedWorkspace,
    [workspaces, unnamedWorkspace],
  );

  // Kept on this screen only, like the column itself.
  const [workspaceColumnWidth, setWorkspaceColumnWidth] = useState<number | null>(null);
  const workspaceMinWidth = CUSTOM_COLUMN_MIN_WIDTH_BY_TYPE[WORKSPACE_FIELD.type];
  const workspaceColumnLabel = t(WORKSPACE_FIELD.labelKey);
  const shownColumnDefs = useMemo(
    () =>
      showsWorkspace
        ? {
            ...layout.columnDefs,
            [WORKSPACE_FIELD.id]: {
              label: workspaceColumnLabel,
              minWidth: workspaceMinWidth,
              type: WORKSPACE_FIELD.type,
            },
          }
        : layout.columnDefs,
    [showsWorkspace, layout.columnDefs, workspaceColumnLabel, workspaceMinWidth],
  );
  const shownColumnWidths = useMemo(
    () =>
      showsWorkspace && workspaceColumnWidth !== null
        ? { ...layout.columnWidths, [WORKSPACE_FIELD.id]: workspaceColumnWidth }
        : layout.columnWidths,
    [showsWorkspace, layout.columnWidths, workspaceColumnWidth],
  );
  const { setColumnWidth: setLayoutColumnWidth } = layout;
  const setColumnWidth = useCallback(
    (columnId: string, width: number) => {
      if (columnId === WORKSPACE_FIELD.id) {
        setWorkspaceColumnWidth(Math.max(width, workspaceMinWidth));
      } else {
        setLayoutColumnWidth(columnId, width);
      }
    },
    [setLayoutColumnWidth, workspaceMinWidth],
  );
  const shownLayout: BoardLayout = showsWorkspace
    ? {
        ...layout,
        columnOrder: queryColumnOrder,
        columnDefs: shownColumnDefs,
        columnWidths: shownColumnWidths,
        setColumnWidth,
        tableMinContentWidth:
          layout.tableMinContentWidth + (workspaceColumnWidth ?? workspaceMinWidth),
      }
    : layout;

  const optionLabels = useFilterOptionLabels();
  const labels = useMemo<RecordLabels>(
    () => ({
      field: (field) => t(field.labelKey),
      // The response's own directory first: it is the only one that can name a
      // creator who is not a member here, which is most of what a filter list
      // over the whole matching set turns out to contain.
      person: (id) => users.get(id)?.name ?? optionLabels.personLabel(id),
      workspace: workspaceLabel,
      dateBuckets: optionLabels.dateBuckets,
      noValue: t('trackersBoard.useRecordBoard.noValue'),
    }),
    [t, optionLabels, users, workspaceLabel],
  );

  const filterColumns = useMemo(
    () => buildRecordFilterColumns(shownFields, valueCounts, labels),
    [shownFields, valueCounts, labels],
  );
  const filterValueCounts = useMemo(
    () =>
      serverFilterValueCounts(
        valueCounts,
        filterColumns.map((column) => column.id),
      ),
    [valueCounts, filterColumns],
  );

  /**
   * Groups, and their sizes, straight from the server's counts. The bucket set
   * is every value it counted — not every value the loaded page happens to show
   * — so a status whose rows are all on page nine still gets a header.
   */
  const groups = useMemo<RecordGroup[]>(() => {
    const rowsByValue = new Map<string, TrackerRow[]>();
    if (groupBy) {
      for (const row of rows) {
        const raw = row.fields[groupBy];
        const key = typeof raw === 'string' && raw ? raw : NO_VALUE;
        const bucket = rowsByValue.get(key);
        if (bucket) bucket.push(row);
        else rowsByValue.set(key, [row]);
      }
    }

    const counts = countsForField(valueCounts, groupBy);
    if (!groupBy || counts.size === 0) {
      return [
        {
          id: NO_GROUP_ID,
          title: tracker.name,
          rows,
          count: total,
        },
      ];
    }

    const field = fieldById.get(groupBy);
    return [...counts.entries()].map(([value, count]) => ({
      id: value,
      title: value === NO_VALUE ? labels.noValue : field ? valueLabel(field, value, labels) : value,
      color: value === NO_VALUE ? NO_GROUP_COLOR : undefined,
      rows: rowsByValue.get(value) ?? [],
      count,
    }));
  }, [groupBy, rows, valueCounts, fieldById, labels, total, tracker.name]);

  const groupByOptions = useMemo(
    () => groupableFields(fields).map((field) => ({ value: field.id, label: t(field.labelKey) })),
    [fields, t],
  );

  const updateRow = useUpdateTrackerRowMutation({ workspaceId, trackerId });

  const rowByKey = useMemo(() => new Map(rows.map((row) => [row.rowKey, row])), [rows]);

  const isEditable = useCallback(
    (columnId: string, rowKey?: string) => {
      const field = fieldById.get(columnId);
      if (field && field.writable !== true) return false;
      if (rowKey === undefined) return true;
      const row = rowByKey.get(rowKey);
      // The server holds even a board column on another workspace's record to
      // the right to edit that record.
      if (!field && !(row && isForeignRow(row, workspaceId))) return true;
      return row?.canEdit !== false;
    },
    [fieldById, rowByKey, workspaceId],
  );

  const isSortable = useCallback(
    (columnId: string) => {
      const field = fieldById.get(columnId);
      return field ? isRecordSortable(field) : false;
    },
    [fieldById],
  );

  const columnMenu = useCallback(
    (columnId: string) =>
      columnId === WORKSPACE_FIELD.id
        ? WORKSPACE_COLUMN_MENU
        : fieldById.has(columnId)
          ? RECORD_FIELD_MENU
          : CUSTOM_COLUMN_MENU,
    [fieldById],
  );

  /** What to call the record in a notice; empty while it has no name yet. */
  const recordName = useCallback(
    (rowKey: string) => {
      const raw = titleField ? rowByKey.get(rowKey)?.fields[titleField] : null;
      return typeof raw === 'string' ? raw : '';
    },
    [rowByKey, titleField],
  );

  const setCellValue = useCallback(
    (rowKey: string, columnId: string, value: unknown) => {
      if (!isEditable(columnId, rowKey)) return;
      const field = fieldById.get(columnId);
      if (field) {
        const fieldLabel = t(field.labelKey);
        // Refused here rather than per control: an emptied editor, a pasted
        // empty value and the Kanban no-status lane all reach this write.
        if (value == null && field.nullable !== true) {
          notificationService.showError(
            t('trackersBoard.useRecordBoard.notSaved', { field: fieldLabel }),
            { description: t('trackersBoard.useRecordBoard.refusedInvalidFieldValue') },
          );
          return;
        }
        // Editing a record field changes the record, not just this board, and
        // nothing else on the page says so. The failure case is what makes this
        // load-bearing: the mutation rolls its optimistic patch back, so without
        // the notice the cell simply reverts and reads as the board ignoring
        // the edit.
        const record = recordName(rowKey);
        updateRow.mutate(
          { rowKey, body: { fields: { [columnId]: value } } },
          {
            onSuccess: () =>
              notificationService.showSuccess(
                record
                  ? t('trackersBoard.useRecordBoard.savedOnRecord', { field: fieldLabel, record })
                  : t('trackersBoard.useRecordBoard.saved', { field: fieldLabel }),
              ),
            onError: (error) =>
              notificationService.showError(...rowWriteRefusalNotice(t, error, fieldLabel)),
          },
        );
        return;
      }
      // Only the edited cell goes out. The server merges it into whatever the
      // row holds now, so a rule that wrote another column between this board's
      // last read and this write keeps its value instead of being replaced by a
      // map that predates it.
      const columnLabel = layout.customColumns.find((column) => column.id === columnId)?.label;
      updateRow.mutate(
        { rowKey, body: { customColumnValues: { [columnId]: value ?? null } } },
        {
          // Silent on success, because the cell already shows what was saved
          // and a board column is an ordinary edit. A failure is not silent:
          // the rollback puts the old value back, which on its own reads as the
          // board having ignored the edit.
          onError: (error) =>
            notificationService.showError(...rowWriteRefusalNotice(t, error, columnLabel)),
        },
      );
    },
    [isEditable, fieldById, updateRow, recordName, layout.customColumns, t],
  );

  const setSortId = useCallback((next: string | null) => {
    setSortIdState(next);
    if (next) setColumnSort(null);
  }, []);

  const toggleColumnSort = useCallback((columnId: string) => {
    setColumnSort((current) => cycleColumnSort(current, columnId));
    setSortIdState(null);
  }, []);

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = rowsQuery;
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: TRACKER_QUERY_KEYS.allRows(workspaceId, trackerId),
    });
  }, [queryClient, workspaceId, trackerId]);

  const hasNarrowed = search.trim().length > 0 || Object.keys(filters).length > 0;
  /**
   * "Nothing to show" is only true once there is an answer. `isPending` covers
   * both the first request and the wait for the layout that shapes it, and the
   * error case is excluded on purpose: a board that failed to load has not been
   * told it is empty.
   */
  const settledEmpty = !rowsQuery.isPending && !rowsQuery.isError && total === 0;

  return {
    layout: shownLayout,
    fields: shownFields,
    customColumns: renderColumns,
    titleField,

    rows,
    groups,
    total,
    users,

    isLoading: rowsQuery.isPending,
    isError: rowsQuery.isError,
    isEmpty: settledEmpty && !hasNarrowed,
    isFilteredEmpty: settledEmpty && hasNarrowed,
    isFetching: rowsQuery.isFetching,
    hasMore: hasNextPage,
    isLoadingMore: isFetchingNextPage,
    loadMore,
    refresh,

    searchInput,
    setSearchInput,
    filters,
    setFilters: setStoredFilters,
    filterColumns,
    filterValueCounts,
    uncountedFieldIds: plan.uncountedFieldIds,

    groupBy,
    setGroupBy: layout.setGroupBy,
    groupByOptions,

    sortId,
    setSortId,
    columnSort,
    toggleColumnSort,

    boardWorkspaceId: workspaceId,
    workspaceLabel,

    setCellValue,
    isEditable,
    isSortable,
    columnMenu,
  };
}
